import { type TypedDocumentNode, gql } from '@apollo/client';
import styled from 'styled-components';

import { useTypedParams } from '@sorare/routing';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Portal } from '@sorare/core/src/atoms/layout/Portal';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { TextureBackground } from '@sorare/core/src/atoms/ui/TextureBackground';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import {
  ANY_SPORT_MY_CARDS_COLLECTION_COLLECTIONSLUG,
  ANY_SPORT_MY_CARDS_GALLERY_RARITY_SET_LEAGUE,
} from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { range } from '@sorare/core/src/lib/arrays';
import { COMPETITION_COLORS } from '@sorare/core/src/lib/colors';
import { isType } from '@sorare/core/src/lib/gql';
import { isRarityStr } from '@sorare/core/src/lib/so5';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { CommonCollectionPreview } from 'components/collections/CommonCollectionPreview';
import { CollectionSetSwitcher } from 'components/myCards/CollectionSetSwitcher';
import { Fallback as CollectionSlotsPreviewFallback } from 'components/myCards/LeagueCollectionPreview/CollectionSlotsPreview';
import { LeaguePageHeader } from 'components/myCards/LeaguePageHeader';
import { LeagueSearchInput } from 'components/myCards/LeagueSearchInput';
import {
  useGallerySearch,
  useGallerySearchResults,
} from 'hooks/my-cards/useGallerySearch';

import type {
  LeagueSlugPageQuery,
  LeagueSlugPageQueryVariables,
} from './__generated__/page.graphql';
import type { PageParams } from './__generated__/routeParams';

const Root = styled.div`
  isolation: isolate;
  padding: var(--double-unit) 0;
`;

const Header = styled(Vertical)`
  position: relative;
  isolation: isolate;
`;

const TextureBackgroundWrapper = styled.div`
  & > * {
    width: 100%;
    height: var(--100vh);
  }
  &:after {
    background: linear-gradient(
      180deg,
      rgba(var(--c-rgb-black), 0) 0%,
      var(--c-black) 100%
    );
    content: '';
    position: absolute;
    inset: 0;
    height: var(--100vh);
  }
`;

const CollectionsGrid = styled(Vertical)`
  padding: 0 var(--double-unit);
  @media ${laptopAndAbove} {
    display: grid;
    align-items: stretch;
    grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  }
`;

const Divider = styled.div`
  height: 1px;
  background: var(--c-nd-150);
  margin: var(--unit) 0 var(--double-unit);
`;

const StyledCollectionSetSwitcher = styled(CollectionSetSwitcher)`
  align-self: center;
  padding: var(--double-unit);
`;

const LEAGUE_SLUG_PAGE_QUERY = gql`
  query LeagueSlugPageQuery(
    $leagueSlug: String!
    $setSlug: String!
    $user: String!
    $sport: Sport!
  ) {
    currentUser {
      slug
      competitionCardCollections(
        competitionSlug: $leagueSlug
        cardSetSlug: $setSlug
        sport: $sport
      ) {
        cardCollections {
          slug
          anyTeam {
            slug
            name
            shortName
            code

            styleGuideColor
            ... on Club {
              slug
              domesticLeague {
                slug
                displayName
              }
            }
          }
          ...CommonCollectionPreview_cardCollection
          ...useGallerySearchResults_cardCollection
        }
        ...LeaguePageHeader_competitionCardCollections
      }
    }
  }
  ${CommonCollectionPreview.fragments.cardCollection}
  ${useGallerySearchResults.fragments.cardCollection}
  ${LeaguePageHeader.fragments.competitionCardCollections}
` as TypedDocumentNode<LeagueSlugPageQuery, LeagueSlugPageQueryVariables>;

const LeagueSlugPageComponent = ({ rarity }: { rarity: Rarity }) => {
  const { currentUser } = useCurrentUserContext();
  const { generateSportContextPath, sport } = useSportContext();
  const { league: leagueSlug, set: setSlug } = useTypedParams<PageParams>();
  const { data, loading } = useQuery(LEAGUE_SLUG_PAGE_QUERY, {
    variables: {
      leagueSlug,
      setSlug,
      user: currentUser?.slug || '',
      sport,
    },
  });

  const competitionCardCollections =
    data?.currentUser?.competitionCardCollections.at(0);
  const { cardCollections } = competitionCardCollections || {};
  const anyTeam = cardCollections?.at(0)?.anyTeam;
  const competition = isType(anyTeam, 'Club') ? anyTeam.domesticLeague : null;
  const { search, onSearchChange } = useGallerySearch();
  const results = useGallerySearchResults(search, cardCollections);
  const sortedResults = [...results].sort((a, b) => {
    const isEmptyA =
      a.cardCollection.userCardCollection?.fulfilledSlotsCount === 0;
    const isEmptyB =
      b.cardCollection.userCardCollection?.fulfilledSlotsCount === 0;
    if (isEmptyA && isEmptyB) {
      return 0;
    }
    if (isEmptyA) {
      return 1;
    }
    return -1;
  });
  return (
    <>
      <PageHeader text={competition?.displayName}></PageHeader>
      <Portal id="page-background">
        <TextureBackgroundWrapper>
          <TextureBackground
            background="wave2"
            mode="dark"
            color={COMPETITION_COLORS[leagueSlug] || 'var(--c-nd-150)'}
          />
        </TextureBackgroundWrapper>
      </Portal>
      <Root>
        <Header>
          <LeaguePageHeader
            competitionCardCollections={competitionCardCollections}
            loading={loading}
          />
          <StyledCollectionSetSwitcher
            cardSetSlug={setSlug}
            path={cardSetSlug =>
              generateSportContextPath(
                ANY_SPORT_MY_CARDS_GALLERY_RARITY_SET_LEAGUE,
                {
                  params: {
                    rarity,
                    set: cardSetSlug,
                    league: leagueSlug,
                  },
                }
              )
            }
          />
          <LeagueSearchInput
            onChange={onSearchChange}
            value={search}
            collectionsCount={cardCollections?.length || 0}
          />
          <Divider />
        </Header>
        <CollectionsGrid gap={2}>
          {loading && !cardCollections?.length
            ? range(6).map(i => <CollectionSlotsPreviewFallback key={i} />)
            : sortedResults?.map(({ cardCollection, hitType }) => {
                return (
                  <CommonCollectionPreview
                    key={cardCollection.slug}
                    collection={cardCollection}
                    to={generateSportContextPath(
                      ANY_SPORT_MY_CARDS_COLLECTION_COLLECTIONSLUG,
                      {
                        params: {
                          collectionSlug: cardCollection.slug,
                        },
                      }
                    )}
                    playerNameFilter={
                      hitType === 'player' ? search.trim() : undefined
                    }
                  />
                );
              })}
        </CollectionsGrid>
      </Root>
    </>
  );
};

const LeagueSlugPage = () => {
  const { rarity } = useTypedParams<PageParams>();

  if (!isRarityStr(rarity)) {
    return null;
  }
  return <LeagueSlugPageComponent rarity={rarity} />;
};
export default LeagueSlugPage;
